/**
 * ỨNG LƯƠNG.
 *
 * MONEY IS AN INTEGER OF MINOR UNITS, END TO END. The field below accepts a
 * whole number of đồng and sends it as `amountMinor` unchanged, because VND has
 * exponent 0. No float, no parseFloat, no "10.5" — a request typed as 500000
 * arrives as exactly 500000. The currency comes from the payroll preview (the
 * branch's own currency), not from a hard-coded 'VND'.
 *
 * The screen also shows what an approved advance DOES: it is deducted from the
 * provisional pay estimate as `advanceDeductionMinor`, so the employee sees the
 * consequence before asking rather than on payday.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorMessage, queryKeys, usePayrollPreview, useSalaryAdvances } from '../lib/queries';
import { approvalStatusLabel, approvalStatusTone } from '../lib/labels';
import type { SalaryAdvanceItem, SalaryAdvanceResponse } from '../lib/contracts';
import { formatMinor, formatTimeDate } from '../lib/format';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  KeyValue,
  LoadingState,
  MoneyText,
  SectionHeader,
  StatusBadge,
} from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';
import { useToast } from '../components/Toast';
import { useSyncState } from '../lib/hooks';

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Tiền mặt tại quầy' },
  { value: 'BANK_TRANSFER', label: 'Chuyển khoản' },
  { value: 'E_WALLET', label: 'Ví điện tử' },
  { value: 'PAYROLL_OFFSET', label: 'Trừ vào kỳ lương' },
] as const;

const schema = z.object({
  // `z.coerce.number()` keeps the field a string in the DOM while validating a
  // whole number — the server rejects anything non-integer or non-positive.
  amount: z.coerce
    .number({ invalid_type_error: 'Nhập số tiền.' })
    .int('Số tiền phải là số nguyên.')
    .positive('Số tiền phải lớn hơn 0.')
    .max(1_000_000_000, 'Số tiền vượt quá giới hạn cho phép.'),
  reason: z.string().min(5, 'Lý do phải có ít nhất 5 ký tự.').max(1000),
  paymentMethod: z.enum(['CASH', 'BANK_TRANSFER', 'E_WALLET', 'PAYROLL_OFFSET']),
});

type FormValues = z.infer<typeof schema>;

export function SalaryAdvanceScreen(): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { online } = useSyncState();
  const payroll = usePayrollPreview();
  const advances = useSalaryAdvances();
  const [created, setCreated] = useState<SalaryAdvanceResponse | null>(null);

  const currency = payroll.data?.currency ?? 'VND';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { amount: 0, reason: '', paymentMethod: 'CASH' },
  });

  const amount = watch('amount');
  const method = watch('paymentMethod');

  const submit = useMutation({
    mutationFn: (values: FormValues) =>
      api.createSalaryAdvance({
        amountMinor: values.amount,
        reason: values.reason,
        paymentMethod: values.paymentMethod,
      }),
    onSuccess: (response) => {
      setCreated(response);
      void queryClient.invalidateQueries({ queryKey: queryKeys.salaryAdvances });
      void queryClient.invalidateQueries({ queryKey: queryKeys.payroll });
      toast.success('Đã gửi đề nghị ứng lương', 'Đề nghị đang chờ duyệt.');
    },
    onError: (error) => toast.error('Không gửi được đề nghị', errorMessage(error)),
  });

  const onSubmit = handleSubmit((values) => submit.mutate(values));

  const estimatedNet = payroll.data?.estimatedNetMinor ?? null;
  const exceedsEstimate = estimatedNet !== null && Number.isFinite(amount) && amount > estimatedNet;

  return (
    <div className="pb-8">
      <ScreenHeader title="Ứng lương" />

      {created === null ? null : (
        <Banner tone="success" title="Đã gửi đề nghị">
          Mã đề nghị <strong>{created.salaryAdvanceId}</strong> · trạng thái {created.status}.
        </Banner>
      )}

      {payroll.data === undefined ? null : (
        <Card className="mt-3">
          <KeyValue
            label="Lương tạm tính kỳ này (sau khấu trừ)"
            hint={`${payroll.data.periodStart} → ${payroll.data.periodEnd}`}
            value={<MoneyText amountMinor={payroll.data.estimatedNetMinor} currency={currency} />}
          />
          <KeyValue
            label="Đã ứng chưa hoàn"
            value={<MoneyText amountMinor={payroll.data.advanceDeductionMinor} currency={currency} />}
          />
        </Card>
      )}

      <form onSubmit={(event) => void onSubmit(event)} className="mt-3 space-y-4">
        <Card>
          <label className="label" htmlFor="amount">
            Số tiền muốn ứng ({currency})
          </label>
          <input
            id="amount"
            type="number"
            inputMode="numeric"
            step={1}
            min={1}
            className="field mt-1 text-2xl font-bold tabular-nums"
            {...register('amount')}
          />
          {errors.amount ? <p className="field-error">{errors.amount.message}</p> : null}
          {Number.isFinite(amount) && amount > 0 ? (
            <p className="mt-1 text-sm text-slate-600">Bằng chữ số: {formatMinor(amount, currency)}</p>
          ) : null}

          <div className="mt-3 grid grid-cols-3 gap-2">
            {[500_000, 1_000_000, 2_000_000].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setValue('amount', preset, { shouldValidate: true })}
                className="min-h-touch rounded-2xl border border-slate-200 text-sm font-semibold text-slate-700 active:bg-slate-50"
              >
                {formatMinor(preset, currency)}
              </button>
            ))}
          </div>

          {exceedsEstimate ? (
            <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
              Số tiền này lớn hơn lương tạm tính hiện tại của bạn. Quản lý có thể từ chối hoặc duyệt một phần.
            </p>
          ) : null}
        </Card>

        <Card>
          <p className="label mb-2">Hình thức nhận</p>
          <div className="grid grid-cols-2 gap-2">
            {PAYMENT_METHODS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setValue('paymentMethod', option.value, { shouldValidate: true })}
                className={[
                  'min-h-touch rounded-2xl border px-3 text-sm font-semibold active:bg-slate-50',
                  option.value === method
                    ? 'border-brand-500 bg-brand-50 text-brand-800'
                    : 'border-slate-200 text-slate-700',
                ].join(' ')}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Card>

        <Card>
          <label className="label" htmlFor="advance-reason">
            Lý do
          </label>
          <textarea
            id="advance-reason"
            rows={3}
            className="field mt-1"
            placeholder="Ví dụ: đóng tiền học cho con đầu tháng."
            {...register('reason')}
          />
          {errors.reason ? <p className="field-error">{errors.reason.message}</p> : null}
        </Card>

        <p className="rounded-2xl bg-slate-100 p-3 text-xs text-slate-600">
          Khoản ứng được duyệt sẽ bị trừ vào lương kỳ này (mục “Đã ứng chưa hoàn”). Đề nghị ứng lương được ghi
          vào nhật ký kiểm toán của chi nhánh.
        </p>

        <Button type="submit" size="lg" fullWidth loading={submit.isPending} disabled={!online}>
          Gửi đề nghị ứng lương
        </Button>
      </form>

      {/* --- history: GET /staff/salary-advances -------------------------- */}
      <SectionHeader title="Lịch sử ứng lương" />
      {advances.isLoading ? (
        <LoadingState />
      ) : (advances.data?.advances.length ?? 0) === 0 ? (
        <EmptyState title="Bạn chưa ứng lương lần nào" />
      ) : (
        <ul className="space-y-2">
          {(advances.data?.advances ?? []).map((advance) => (
            <li key={advance.id}>
              <AdvanceCard advance={advance} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ADVANCE_METHOD_LABEL: Readonly<Record<string, string>> = {
  CASH: 'Tiền mặt tại quầy',
  BANK_TRANSFER: 'Chuyển khoản',
  E_WALLET: 'Ví điện tử',
  PAYROLL_OFFSET: 'Trừ vào kỳ lương',
};

function AdvanceCard({ advance }: { readonly advance: SalaryAdvanceItem }): JSX.Element {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lg font-bold tabular-nums text-slate-900">
            <MoneyText amountMinor={advance.amountMinor} currency={advance.currency} />
          </p>
          <p className="text-xs text-slate-400">Đề nghị lúc {formatTimeDate(advance.requestedAt)}</p>
        </div>
        <StatusBadge tone={approvalStatusTone(advance.status)}>{approvalStatusLabel(advance.status)}</StatusBadge>
      </div>

      <p className="mt-2 text-sm text-slate-700">{advance.reason}</p>

      <div className="mt-2 space-y-1 text-sm text-slate-600">
        {advance.paymentMethod === null ? null : (
          <p>Hình thức nhận: {ADVANCE_METHOD_LABEL[advance.paymentMethod] ?? advance.paymentMethod}</p>
        )}
        {advance.disbursedAt === null ? null : <p>Đã chi lúc {formatTimeDate(advance.disbursedAt)}</p>}
        {advance.rejectedReason === null ? null : (
          <p className="text-rose-700">Lý do từ chối: {advance.rejectedReason}</p>
        )}
        {advance.outstandingMinor > 0 && advance.disbursedAt !== null ? (
          <p>
            Còn phải hoàn:{' '}
            <MoneyText amountMinor={advance.outstandingMinor} currency={advance.currency} />
          </p>
        ) : null}
      </div>
    </Card>
  );
}
